import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { fetchArticleBySlug } from '../lib/cms';
import InitialConsultationCallout from '../components/InitialConsultationCallout';

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const data = await fetchArticleBySlug(slug);
      setArticle(data);
      setLoading(false);
    }
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center py-20">
        <div className="text-center space-y-3">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-[#3A4F3D]" aria-hidden="true" />
          <p className="text-sm text-[#5B6B5B]">Indlæser blogindlæg...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center py-20 px-4">
        <div className="text-center max-w-md space-y-4">
          <h1 className="font-serif text-3xl text-[#233424]">Blogindlæg ikke fundet</h1>
          <p className="text-sm text-[#5B6B5B]">
            Vi kunne desværre ikke finde det ønskede blogindlæg.
          </p>
          <Link
            to="/blogindlaeg"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium text-white bg-[#3A4F3D]"
          >
            Se alle blogindlæg
          </Link>
        </div>
      </div>
    );
  }

  const imgSrc = article.image?.[0];
  const shareUrl = encodeURIComponent(`https://www.kerneviden.dk/l/${slug}/`);
  const metaTitle = `${article.title} :: KerneViden`;
  const metaDesc = article.summary || article.title;

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        ogTitle={metaTitle}
        ogDescription={metaDesc}
        ogImage={imgSrc || "https://assets.auxodev.dk/assets/1d30bca3-a748-4ede-8335-828014d68a29/f31895a0-283e-4feb-8720-c86667d599ad_5c85b2c3.png"}
      />

      {/* Breadcrumb Header */}
      <section className="pt-8 pb-10 bg-white border-b border-[#EAE2D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <nav aria-label="Brødkrummer" className="flex items-center gap-2 text-xs text-[#7B8B79]">
            <Link to="/" className="hover:text-[#233424] transition-colors">
              Hjem
            </Link>
            <i className="fa-solid fa-chevron-right text-[9px]" aria-hidden="true" />
            <Link to="/blogindlaeg" className="hover:text-[#233424] transition-colors">
              Blogindlæg
            </Link>
            <i className="fa-solid fa-chevron-right text-[9px]" aria-hidden="true" />
            <span className="text-[#233424] truncate font-medium">{article.title}</span>
          </nav>

          <div className="pt-2">
            <time className="text-xs font-semibold uppercase tracking-wider text-[#738272] block mb-2">
              {article.dateFormatted || article.date}
            </time>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#233424] font-normal tracking-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Article Content */}
      <article className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-8 space-y-8">
              {/* Featured Image */}
              {imgSrc && (
                <div className="rounded-3xl overflow-hidden shadow-[0_16px_45px_rgba(44,62,45,0.06)] bg-[#EFE8DD]">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${imgSrc.replace('https://assets.auxodev.dk/', '')}`}
                    alt={article.title}
                    className="w-full h-auto max-h-[520px] object-cover"
                  />
                </div>
              )}

              {/* Body Prose - Verbatim HTML from scrape */}
              <div
                className="text-[#2D382D] text-base sm:text-lg leading-relaxed space-y-5 prose prose-stone max-w-none [&>h2]:font-serif [&>h2]:text-2xl sm:[&>h2]:text-3xl [&>h2]:text-[#233424] [&>h2]:font-medium [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:font-serif [&>h3]:text-xl [&>h3]:text-[#2C3E2D] [&>h3]:italic [&>p]:mb-4 [&>p>em]:text-[#3B4D3C] [&>p>em]:font-normal"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Social Share & Back Bar */}
              <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <Link
                  to="/blogindlaeg"
                  className="inline-flex items-center text-sm font-semibold text-[#3A4F3D] hover:text-[#233424] transition-colors"
                >
                  <i className="fa-solid fa-arrow-left text-xs mr-2" aria-hidden="true" />
                  <span>Tilbage til alle blogindlæg</span>
                </Link>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#7B8B79] font-medium">Del dette indlæg:</span>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold text-white bg-[#3B5998] hover:bg-[#2D4373] transition-colors shadow-2xs"
                    aria-label="Del på Facebook"
                  >
                    <i className="fa-brands fa-facebook text-sm" aria-hidden="true" />
                    <span>Del</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar with quick info / contact CTA aligned with container */}
            <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E2D9CD] space-y-5">
                <h3 className="font-serif text-xl sm:text-2xl text-[#233424] font-medium">
                  KerneViden &amp; rådgivning
                </h3>
                <p className="text-sm text-[#5B6B5B] leading-relaxed">
                  Har du spørgsmål til emnet, eller ønsker du at høre mere om et skræddersyet forløb for dig eller dit barn?
                </p>
                <div className="pt-2 space-y-3">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full text-sm font-medium text-white bg-[#233424] hover:bg-[#324933] transition-all hover:scale-105 shadow-xs"
                  >
                    Book uforpligtende samtale
                  </Link>
                  <a
                    href="tel:20992149"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-[#233424] hover:bg-[#FAF7F2] border border-[#D5C7B7] transition-all"
                  >
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                    <span>20 99 21 49</span>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Consultation Callout */}
      <InitialConsultationCallout
        title="Ønsker du at høre mere om et forløb?"
        description="Der tilbydes en indledende telefonisk/online samtale gratis."
        ctaText="Book gratis samtale"
      />
    </div>
  );
}
